/** Passport preset helpers — fallbacks only; catalog comes from GET /api/resize-presets */

export const PASSPORT_CATEGORY_ID = 2;

export const DEFAULT_PASSPORT_SPEC = {
  w_mm: 35,
  h_mm: 45,
  dpi: 300,
  head_h_ratio_min: 0.62,
  head_h_ratio_max: 0.69,
  eye_line_from_top: 0.4,
  bg_hex: "#FFFFFF",
  white_background: true,
};

/** Sort passport presets for country picker (all active countries from API). */
export function sortPassportPresets(presets = []) {
  return [...presets].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** @deprecated use sortPassportPresets */
export function filterPassportV1Presets(presets = []) {
  return sortPassportPresets(presets);
}

export function getPresetDisplayLabel(preset, categoryDefault = "") {
  if (!preset) return categoryDefault || "";
  const fromMeta =
    preset.display_size_label ||
    preset.metadata?.display?.size_label;
  if (fromMeta) return fromMeta;
  const spec = getPassportSpec(preset);
  if (spec?.w_mm != null && spec?.h_mm != null) {
    return formatSizeMmDisplay(spec);
  }
  return preset.label || categoryDefault || "";
}

/** Subtitle in preset picker listbox — size label plus aspect ratio when available. */
export function getPresetPickerSubLabel(preset, _categoryId, categoryDefault = "") {
  const sizeLabel = getPresetDisplayLabel(preset, categoryDefault);
  const aspectRatio = getPresetAspectRatio(preset);
  if (!aspectRatio || sizeLabel.includes(aspectRatio)) {
    return sizeLabel;
  }
  return `${sizeLabel}, ${aspectRatio}`;
}

function getPresetAspectRatio(preset) {
  const fromField = preset?.aspect_ratio?.trim();
  if (fromField) return fromField;
  const label = preset?.label || "";
  const commaIdx = label.indexOf(",");
  if (commaIdx >= 0) return label.slice(commaIdx + 1).trim();
  return "";
}

export function getPassportSpec(preset) {
  return preset?.passport_spec || preset?.metadata?.passport || null;
}

export function resolvePassportSpec(preset) {
  const spec = { ...DEFAULT_PASSPORT_SPEC, ...(getPassportSpec(preset) || {}) };
  return {
    ...spec,
    bg_hex: "#FFFFFF",
    white_background: true,
  };
}

/** Numeric DPI from catalog (not dpi_label range text). */
export function getPassportDpiDisplay(spec) {
  if (!spec || spec.dpi == null) return "";
  return String(spec.dpi);
}

export function clampRatio(value, min = 0, max = 1) {
  const n = Number(value);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

export function formatSizeMm(spec) {
  const w = spec?.w_mm ?? DEFAULT_PASSPORT_SPEC.w_mm;
  const h = spec?.h_mm ?? DEFAULT_PASSPORT_SPEC.h_mm;
  return `${w} × ${h} mm`;
}

/** User-facing mm size (matches catalog metadata, e.g. 51 mm × 51 mm). */
export function formatSizeMmDisplay(spec) {
  const w = spec?.w_mm ?? DEFAULT_PASSPORT_SPEC.w_mm;
  const h = spec?.h_mm ?? DEFAULT_PASSPORT_SPEC.h_mm;
  return `${w} mm × ${h} mm`;
}

/** Short label for compact passport UI — derived from catalog background_label. */
export function backgroundCompactLabel(spec) {
  const label = spec?.background_label?.trim();
  if (label) {
    if (/^white\b/i.test(label)) return "White";
    return label;
  }
  const hex = (spec?.bg_hex || "#FFFFFF").toUpperCase();
  if (hex === "#FFFFFF" || hex === "#FFF") return "White";
  return hex;
}

export function backgroundDisplayLabel(spec) {
  if (spec?.background_label) return spec.background_label;
  const hex = (spec?.bg_hex || "#FFFFFF").toUpperCase();
  if (hex === "#FFFFFF" || hex === "#FFF") return "White";
  return hex;
}

const PASSPORT_COUNTRY_ALIASES = {
  indian: "India",
  uk: "United Kingdom",
};

export function extractPassportCountryName(presetName) {
  const name = String(presetName || "").trim();
  if (!name) return "your country";
  const parts = name.split(/\s+/);
  const stripped =
    parts.length >= 2 && parts[parts.length - 1].toLowerCase() === "passport"
      ? parts.slice(0, -1).join(" ") || "your country"
      : name;
  return PASSPORT_COUNTRY_ALIASES[stripped.toLowerCase()] || stripped;
}

/** Loading-screen country. Prefers the catalog display name over a demonym. */
export function passportCountryLabel(preset) {
  if (!preset) return "—";
  const raw =
    preset.display_name ||
    preset.country_name ||
    preset.country ||
    preset.name ||
    "";
  const label = extractPassportCountryName(raw);
  return !label || label === "your country" ? "—" : label;
}

/** Tooltip copy — uses full catalog background_label, not generic "White" from API. */
export function getPassportBackgroundTooltip(spec, helperText = {}, presetName = "") {
  const override = helperText?.background?.trim();
  const backgroundLabel = spec?.background_label?.trim();

  if (backgroundLabel) {
    if (override && override.includes(backgroundLabel)) {
      return override;
    }
    const country = extractPassportCountryName(presetName);
    return `${backgroundLabel} background is required for ${country} passport photos.`;
  }

  return override || null;
}

export function mmToPx(mm, dpi = 300) {
  return Math.round((mm / 25.4) * dpi);
}

/** Match a saved passport output to a catalog preset from pixel dimensions at DPI. */
export function findPassportPresetByDimensions(
  presets = [],
  width,
  height,
  tolerancePx = 3
) {
  if (!width || !height || !presets.length) return null;

  return (
    presets.find((preset) => {
      const spec = resolvePassportSpec(preset);
      const ew = mmToPx(spec.w_mm, spec.dpi);
      const eh = mmToPx(spec.h_mm, spec.dpi);
      const matchNormal =
        Math.abs(width - ew) <= tolerancePx && Math.abs(height - eh) <= tolerancePx;
      const matchRotated =
        Math.abs(width - eh) <= tolerancePx && Math.abs(height - ew) <= tolerancePx;
      return matchNormal || matchRotated;
    }) || null
  );
}
